import axios from "axios";
import { 
  InventarioType,
  EstoqueInventarioType,
  AtualizarLoteDto,
  ExcluirLoteDto,
  CriarInventarioDto,
  AlterarDataInventarioDto,
  PesquisarProdutosDto,
  ProdutoParaInventarioType,
  CriarTransmissaoDto,
  TransmissaoType,
  CriarMovimentoDto,
  MovimentoType,
  EstatisticasMovimentosType,
  JanelaTemporalType
} from "data/sngpc";

// Função para criar o config com header empresaId
const createConfig = (empresaId: number) => ({
  headers: {
    empresaId: empresaId.toString()
  }
});

// Serviços para Inventário
export const sngpcService = {
  // === INVENTÁRIO ===
  async obterInventarioAtivo(empresaId: number): Promise<{ data: InventarioType | null; isSuccess: boolean; message?: string }> {
    const response = await axios.get("/api/sngpc/inventario", createConfig(empresaId));
    return response.data;
  },

  async criarInventario(dto: CriarInventarioDto, empresaId: number): Promise<{ data: any; isSuccess: boolean; message?: string }> {
    try {
      console.log('Enviando para backend:', dto, 'EmpresaId:', empresaId);
      const response = await axios.post("/api/sngpc/inventario", dto, createConfig(empresaId));
      console.log('Resposta do backend (sucesso):', response.data);
      return response.data;
    } catch (error: any) {
      console.log('Erro capturado no axios:', error);
      if (error.response && error.response.data) {
        console.log('Dados do erro:', error.response.data);
        return error.response.data;
      }
      throw error;
    }
  },

  async alterarDataInventario(dto: AlterarDataInventarioDto, empresaId: number): Promise<{ isSuccess: boolean; data: any; message?: string }> {
    const response = await axios.put("/api/sngpc/inventario/data", dto, createConfig(empresaId));
    return response.data;
  },

  async pesquisarProdutosParaInventario(dto: PesquisarProdutosDto, empresaId: number): Promise<{ isSuccess: boolean; data: ProdutoParaInventarioType[]; total: number; pageIndex: number; pageSize: number; message?: string }> {
    const params = new URLSearchParams();
    if (dto.termo) params.append('termo', dto.termo);
    params.append('pageIndex', dto.pageIndex.toString());
    params.append('pageSize', dto.pageSize.toString());
    
    const response = await axios.get(`/api/sngpc/inventario/produtos/pesquisar?${params.toString()}`, createConfig(empresaId));
    return response.data;
  },

  async pesquisarProdutosInfinito(pageParam: number, termo: string | undefined, empresaId: number): Promise<{ isSuccess: boolean; data: ProdutoParaInventarioType[]; total: number; pageIndex: number; pageSize: number; message?: string }> {
    const params = new URLSearchParams();
    if (termo) params.append('termo', termo);
    params.append('pageIndex', pageParam.toString());
    params.append('pageSize', '20'); // Aumentar pageSize para melhor performance
    
    const response = await axios.get(`/api/sngpc/inventario/produtos/pesquisar?${params.toString()}`, createConfig(empresaId));
    return response.data;
  },

  async incluirProdutos(itens: Partial<EstoqueInventarioType>[], empresaId: number): Promise<{ data: any; isSuccess: boolean; message?: string }> {
    const response = await axios.post("/api/sngpc/inventario/produtos", itens, createConfig(empresaId));
    return response.data;
  },

  async importarEstoqueControlado(empresaId: number): Promise<{
    isSuccess: boolean; data: any; message?: string 
}> {
    const response = await axios.post("/api/sngpc/inventario/importar-estoque-controlado", {}, createConfig(empresaId));
    return response.data;
  },

  async atualizarLote(dto: AtualizarLoteDto, empresaId: number): Promise<{
    isSuccess: any; data: any; message?: string 
}> {
    const response = await axios.put("/api/sngpc/inventario/lote", dto, createConfig(empresaId));
    return response.data;
  },

  async excluirLote(dto: ExcluirLoteDto, empresaId: number): Promise<{
    isSuccess: any; data: any; message?: string 
}> {
    const config = createConfig(empresaId);
    const response = await axios.delete("/api/sngpc/inventario/lote", { 
      ...config,
      data: dto 
    });
    return response.data;
  },

  async exportarXmlInventario(empresaId: number): Promise<Blob> {
    const response = await axios.get("/api/sngpc/inventario/xml", {
      ...createConfig(empresaId),
      responseType: "blob",
    });
    return response.data;
  },

  async validarInventario(empresaId: number): Promise<{
    isSuccess: any; data: any; message?: string 
}> {
    const response = await axios.post("/api/sngpc/inventario/validar", {}, createConfig(empresaId));
    return response.data;
  },

  async finalizarInventario(empresaId: number): Promise<{
    isSuccess: any; data: any; message?: string 
}> {
    const response = await axios.post("/api/sngpc/inventario/finalizar", {}, createConfig(empresaId));
    return response.data;
  },

  // === TRANSMISSÕES ===
  async criarTransmissao(dto: CriarTransmissaoDto, empresaId: number): Promise<{
    isSuccess: any; data: TransmissaoType; message?: string 
}> {
    const response = await axios.post("/api/sngpc/transmissao", dto, createConfig(empresaId));
    return response.data;
  },

  async removerUltimaTransmissao(inventarioId: number, empresaId: number): Promise<{
    isSuccess: any; data: any; message?: string 
}> {
    const response = await axios.delete(`/api/sngpc/transmissao/${inventarioId}/ultima`, createConfig(empresaId));
    return response.data;
  },

  async obterProximaDataTransmissao(inventarioId: number, empresaId: number): Promise<{
    isSuccess: any; data: { proximaData: string }; message?: string 
}> {
    const response = await axios.get("/api/sngpc/transmissao/proxima-data", {
      ...createConfig(empresaId),
      params: { inventarioId }
    });
    return response.data;
  },

  async listarTransmissoes(inventarioId: number, empresaId: number): Promise<{
    isSuccess: any; data: TransmissaoType[]; message?: string 
}> {
    const response = await axios.get("/api/sngpc/transmissao", {
      ...createConfig(empresaId),
      params: { inventarioId }
    });
    return response.data;
  },

  // === MOVIMENTOS ===
  async criarMovimento(dto: CriarMovimentoDto, empresaId: number): Promise<{
    isSuccess: any; data: MovimentoType; message?: string 
}> {
    const response = await axios.post("/api/sngpc/movimento", dto, createConfig(empresaId));
    return response.data;
  },

  async obterMovimento(id: number, empresaId: number): Promise<{
    isSuccess: any; data: MovimentoType; message?: string 
}> {
    const response = await axios.get(`/api/sngpc/movimento/${id}`, createConfig(empresaId));
    return response.data;
  },

  async listarMovimentos(inventarioId: number, empresaId: number, filtros?: {
    tipo?: string;
    dataInicio?: string;
    dataFim?: string;
    termo?: string;
  }): Promise<{
    isSuccess: any; data: MovimentoType[]; message?: string 
}> {
    const params = new URLSearchParams();
    params.append('inventarioId', inventarioId.toString());
    
    if (filtros?.tipo) params.append('tipo', filtros.tipo);
    if (filtros?.dataInicio) params.append('dataInicio', filtros.dataInicio);
    if (filtros?.dataFim) params.append('dataFim', filtros.dataFim);
    if (filtros?.termo) params.append('termo', filtros.termo);

    const response = await axios.get(`/api/sngpc/movimentos?${params.toString()}`, createConfig(empresaId));
    return response.data;
  },

  async editarMovimento(id: number, dto: Partial<CriarMovimentoDto>, empresaId: number): Promise<{
    isSuccess: any; data: MovimentoType; message?: string 
}> {
    const response = await axios.put(`/api/sngpc/movimento/${id}`, dto, createConfig(empresaId));
    return response.data;
  },

  async excluirMovimento(id: number, empresaId: number): Promise<{
    isSuccess: any; message?: string 
}> {
    const response = await axios.delete(`/api/sngpc/movimento/${id}`, createConfig(empresaId));
    return response.data;
  },

  async obterEstatisticasMovimentos(inventarioId: number, empresaId: number): Promise<{
    isSuccess: any; data: EstatisticasMovimentosType; message?: string 
}> {
    const response = await axios.get(`/api/sngpc/movimentos/estatisticas?inventarioId=${inventarioId}`, createConfig(empresaId));
    return response.data;
  },

  async obterJanelaTemporalAtiva(inventarioId: number, empresaId: number): Promise<{
    isSuccess: any; data: JanelaTemporalType; message?: string 
}> {
    const response = await axios.get(`/api/sngpc/movimentos/janela-ativa?inventarioId=${inventarioId}`, createConfig(empresaId));
    return response.data;
  },

  async importarLotesVendidos(inventarioId: number, dataInicio: string, dataFim: string, empresaId: number): Promise<{
    isSuccess: any; data: any[]; message?: string 
}> {
    const response = await axios.get(`/api/sngpc/movimentos/importar-vendas?inventarioId=${inventarioId}&dataInicio=${dataInicio}&dataFim=${dataFim}`, createConfig(empresaId));
    return response.data;
  },

  async importarLotesComprados(inventarioId: number, dataInicio: string, dataFim: string, empresaId: number): Promise<{
    isSuccess: any; data: any[]; message?: string 
}> {
    const response = await axios.get(`/api/sngpc/movimentos/importar-compras?inventarioId=${inventarioId}&dataInicio=${dataInicio}&dataFim=${dataFim}`, createConfig(empresaId));
    return response.data;
  },
}; 